/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createLogPrefix, logDebug } from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import { cblSignIn, type CblSignInDeps } from '@amzn/vinyl-amzn/cbl'
import { AuthStatus } from '@amzn/amp-playback'
import { localSignInPersistence } from '@/core/signInPersistence'
import { TokenAuth } from '@/auth/TokenAuth'

export interface CblAuthDeps extends Omit<CblSignInDeps, 'codeData'> {
    /**
     * Resolves the code data at sign-in time. Its source (`getDeviceInfo`) is
     * only populated after `initializeDeviceInfo`, so it must not be forced at
     * construction — the controller is built during the boot import graph,
     * before that runs.
     */
    readonly codeDataProvider: () => CblSignInDeps['codeData']
}

/**
 * How long a failed attempt waits before settling SIGNED_OUT. The public code
 * prompt re-requests a code on that transition and the codepair request is not
 * itself retried, so an immediate settle would spin one request per event-loop
 * turn on an offline device.
 */
export const RETRY_DELAY_SECONDS = 5

/**
 * Manages authentication state with code-based linking.
 * Sets the auth token and DMLS options on the player.
 */
export class CblAuth extends TokenAuth {
    logPrefix = createLogPrefix('CblAuth')

    private errorHandler = createErrorHandler(this)

    /**
     * Generation of the latest sign-in intent. Only the attempt still matching
     * it may commit a status, so a sign-out (or a newer attempt) discards an
     * in-flight one whose late result would otherwise resurrect the session it
     * was signed out of.
     */
    private signInGeneration = 0

    constructor(private readonly deps: CblAuthDeps) {
        super(AuthStatus.SIGNED_OUT)
    }

    /**
     * Prompts for sign-in (may use local storage to retrieve previous auth).
     */
    signIn() {
        logDebug(this, 'signIn')
        if (this._status.value !== AuthStatus.SIGNED_OUT) return
        this.setStatus(AuthStatus.PENDING)
        const generation = ++this.signInGeneration
        const { codeDataProvider, ...signInDeps } = this.deps
        ;(async () => {
            const authTokenProvider = await cblSignIn({
                ...signInDeps,
                codeData: codeDataProvider(),
            })
            if (generation !== this.signInGeneration) return
            this._authTokenProvider = authTokenProvider
            logDebug(this, 'signInComplete')
            this.setStatus(AuthStatus.SIGNED_IN)
        })().catch((error) => {
            this.errorHandler(error)
            // A failure MUST settle back to SIGNED_OUT: `signIn` early-returns
            // unless SIGNED_OUT and the code prompt only re-requests on that
            // transition, so staying PENDING wedges the device with no retry.
            setTimeout(() => {
                if (generation === this.signInGeneration) {
                    this.setStatus(AuthStatus.SIGNED_OUT)
                }
            }, RETRY_DELAY_SECONDS * 1000)
        })
    }

    /**
     * Signs out, clearing the currently authenticated user.
     * Emits a 'signOut' event.
     */
    signOut() {
        logDebug(this, 'signOut')
        ++this.signInGeneration
        localSignInPersistence.remove().catch(this.errorHandler)
        this._authTokenProvider = null
        this.setStatus(AuthStatus.SIGNED_OUT)
    }
}
